/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

export enum DIR {
	LTR = "ltr",
	RTL = "rtl",
}

export type Direction = "ltr" | "rtl";

/**
 * RTL languages, by BCP 47 language subtag (ISO 639-1 and -3).
 *
 * Matched on the subtag rather than the full tag. The previous list held exact
 * strings (`["ar", "ar-ar", "ar-AR"]`), so every regional Arabic tag — `ar-SA`,
 * `ar-AE` — resolved LTR, as did Hebrew, Persian and Urdu entirely.
 *
 * Latent rather than live: the only RTL locale any site currently ships is bare
 * `ar`, which the old list already matched, and `ar-SA` is redirected to `ar` by
 * `commonUnsupportedLocaleMapping`. So this is correctness for the next RTL
 * locale added, not a fix for something users are hitting today.
 *
 * What widening this set does and doesn't change, because it is easy to
 * under-test:
 *
 * - `getDirection` feeds `<html dir>` only. Every layout still hardcodes
 *   `dir="ltr"` on `#cosmos-app`, and `globals.css` has a nested `[dir="ltr"]`
 *   rule that outranks `html[dir="rtl"] *`, so page layout does not flip.
 *   Removing that hardcode is separate work.
 * - `isLangRtl` also backs `useIsRtl` / `useDirection` (`../hooks/direction.hooks`),
 *   and those DO mirror rendered output — `Animate`, `EdsIcon.inner`,
 *   `HeroAlpha.HeroImage` and `LogoTitleDescButtonStack` branch on them for icon
 *   and animation direction. So a newly-matching locale changes those components
 *   even though the page frame stays LTR. Regression-test them when enabling one.
 *
 * Each entry checked against ICU (`new Intl.Locale(x).maximize().getTextInfo()`).
 * `ku` is deliberately absent: it maximizes to `ku-Latn-TR`, which is LTR — RTL
 * Kurdish is `ckb` (below) or `ku-Arab`, which is handled by the script table.
 *
 * Deliberately a static list rather than calling `getTextInfo()` at runtime:
 * `getDirection` runs server-side computing `<html dir>` while `useDirection`
 * runs in the browser, so an engine-dependent answer could disagree between
 * Node's ICU and the client's.
 */
const RTL_LANGUAGE_SUBTAGS = new Set([
	"ar", // Arabic
	"arc", // Aramaic
	"ckb", // Central Kurdish (Sorani)
	"dv", // Divehi
	"fa", // Persian
	"he", // Hebrew
	"iw", // Hebrew, deprecated ISO 639-1 — still emitted by some clients
	"ji", // Yiddish, deprecated ISO 639-1
	"ks", // Kashmiri
	"nqo", // N'Ko
	"ps", // Pashto
	"sd", // Sindhi
	"syr", // Syriac
	"ug", // Uyghur
	"ur", // Urdu
	"yi", // Yiddish
]);

/**
 * RTL scripts, by BCP 47 script subtag (ISO 15924).
 *
 * Unicode scripts follow ICU. `Aran` and the `Syr*` variants inherit direction
 * from their Arabic and Syriac parents because ICU has no direction data for
 * those ISO 15924 variant codes.
 */
const RTL_SCRIPT_SUBTAGS = new Set([
	"adlm",
	"arab",
	"aran",
	"armi",
	"avst",
	"chrs",
	"cprt",
	"elym",
	"gara",
	"hatr",
	"hebr",
	"hung",
	"khar",
	"lydi",
	"mand",
	"mani",
	"mend",
	"merc",
	"mero",
	"narb",
	"nbat",
	"nkoo",
	"orkh",
	"ougr",
	"palm",
	"phli",
	"phlp",
	"phnx",
	"prti",
	"rohg",
	"samr",
	"sarb",
	"sidt",
	"sogd",
	"sogo",
	"syrc",
	"syre",
	"syrj",
	"syrn",
	"thaa",
	"yezi",
]);

/**
 * Is this locale written right-to-left?
 *
 * Accepts a full BCP 47 tag or a bare subtag, case-insensitively, and tolerates
 * both `-` and `_` separators since callers pass values straight through from
 * route params and CMS fields. An explicit script subtag overrides the language:
 * known RTL scripts resolve RTL and any other script resolves LTR.
 *
 * @param locale - e.g. "ar", "ar-SA", "he_IL", "en-US". Widened to accept
 *        null/undefined because callers pass route params straight through.
 */
export const isLangRtl = (locale: string | null | undefined): boolean => {
	if (!locale || typeof locale !== "string") return false;
	const [languageSubtag, ...subtags] = locale.trim().toLowerCase().split(/[-_]/);
	let scriptIndex = 0;
	// A 2–3 letter language may be followed by up to three 3-letter extlang subtags.
	if (/^[a-z]{2,3}$/.test(languageSubtag)) {
		while (scriptIndex < 3 && /^[a-z]{3}$/.test(subtags[scriptIndex])) scriptIndex += 1;
	}
	const scriptSubtag = subtags[scriptIndex];
	if (/^[a-z]{4}$/.test(scriptSubtag)) return RTL_SCRIPT_SUBTAGS.has(scriptSubtag);
	return RTL_LANGUAGE_SUBTAGS.has(languageSubtag);
};

export const getDirection = (locale: string | null | undefined): Direction => {
	const isRtl = isLangRtl(locale);
	return isRtl ? "rtl" : "ltr";
};
